import { Component } from "@angular/core";
import { TenantDataService } from "../service/tenant-data.service";
import { tap, map, catchError } from "rxjs/operators";
import { Tenant } from "../models/tenant";
import { State } from "@progress/kendo-data-query";
import { FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { Observable, of } from 'rxjs';
import { TenantEnvironmentDataService } from '../service/tenant-environment-data.service';
import { InlineEditGridComponent } from 'src/app/core/inline-edit-grid.component';

@Component({
  selector: "app-tenants",
  templateUrl: "./tenants.component.html",
  styleUrls: ["./tenants.component.scss"],
})
export class TenantsComponent extends InlineEditGridComponent {
  public gridState: State = {
    sort: [
      {
        field: "Name",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 10,
  };

  constructor(
    private dataService: TenantDataService,
    private tenantEnvDataService: TenantEnvironmentDataService,
    private dialogService: DialogService
  ) {
    super();
  }

  protected refresh() {
    this.view = this.dataService
      .get(this.gridState, "")
      .pipe(tap(() => (this.loading = false)));
  }

  public addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(0),
      Code: this.CreateCodeControl(''),
      Name: this.CreateNameControl(''),
      Description: this.CreateDescriptionControl('')
    });
    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(dataItem.Id),
      Code: this.CreateCodeControl(dataItem.Code),
      Name: this.CreateNameControl(dataItem.Name),
      Description: this.CreateDescriptionControl(dataItem.Description)
    });

    this.editedRowIndex = rowIndex;

    sender.editRow(rowIndex, this.formGroup);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }) {
    const entity: Tenant = formGroup.value;
    this.loading = true;
    this.refreshObservable(this.dataService.update(entity, isNew));

    sender.closeRow(rowIndex);
  }

  public removeHandler({ dataItem }) {
    const deleteAction = { text: "Delete", primary: true };
    const cancelAction = { text: "Cancel" };

    this.tenantEnvDataService.linkCount(+dataItem.Id)
    .subscribe(ec => 
      {
        if (+ec['@odata.count'] > 0) {
          let phrase = 'environment';
          if (+ec['@odata.count'] > 1) {
            phrase = phrase + 's';
          }
          this.dialogService.open({
            title: "Cannot Delete",
            content: `This tenant is associated with ${ec['@odata.count']} ${phrase}`,
            actions: [cancelAction],
          });
          return;
        }

        const dialog: DialogRef = this.dialogService.open({
          title: "Please confirm",
          content: "Deleting this tenant cannot be undone.",
          actions: [cancelAction, deleteAction],
        });
    
        dialog.result.subscribe((result) => {
          if (result === deleteAction) {
            this.refreshObservable(this.dataService.delete(dataItem.Id));
          }
        });
      });
  }

  private CreateCodeControl(code){
    return new FormControl(code ?? 'Code', {updateOn: 'blur', validators: [Validators.required, Validators.minLength(3), Validators.maxLength(25), Validators.pattern('^[0-9a-zA-Z\._\-]*$')], asyncValidators: [this.UniqueCode.bind(this)]});
  }

  private CreateNameControl(name){
    return new FormControl(name ?? 'Tenant Name', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern('^[0-9a-zA-Z \.:_\-]*$')]);
  }

  private CreateDescriptionControl(description){
    return new FormControl(description ?? 'Tenant Description', [Validators.maxLength(200), Validators.pattern('^[0-9a-zA-Z \.:;_\-]*$')]);
  }
  
  UniqueCode(ctl: AbstractControl) : Observable<ValidationErrors | null> {
    if (ctl.pristine)
      return of(null);

    let idCtl = ctl.parent.controls['Id'];

    return this.dataService.tenantCount(+idCtl.value, ctl.value)
    .pipe(
      map(
        r => 
        +r["@odata.count"] > 0 ? { unique: true } : null),
      catchError(() => of(null))
    );
  }   
}
